import { FormErrors } from '@mantine/form';
import { get } from 'lodash';

import { DeclarationDocumentsEnum } from '@e-origin/shared';

import { hasInnerWhitespace, INNER_WHITESPACE_ERROR } from '@utils';
import { DeclarationDocumentSchema, FormDocumentsDeclarationType } from './DeclarationDocumentFormSchema';

type DocumentFieldConfig = DeclarationDocumentSchema[string]['fields'][string];

export interface DocumentFormValues {
  documentType?: DeclarationDocumentsEnum | null;
  header?: boolean;
}

// only fields that are actually rendered may be validated, otherwise a hidden required field
// would block the form with an error the user cannot see
export const isFieldRendered = (field: DocumentFieldConfig, declarationType: FormDocumentsDeclarationType, isHeader: boolean): boolean => {
  const appliesToDeclarationType = [undefined, FormDocumentsDeclarationType.ALL, declarationType].includes(field.declarationType);
  const isShown = !field.show || field.show({ isHeader });

  return appliesToDeclarationType && isShown;
};

// noWhitespace also trims, trimWhitespace only trims
export const shouldTrimField = (field: DocumentFieldConfig): boolean => Boolean(field.noWhitespace || field.trimWhitespace);

const getFieldError = (field: DocumentFieldConfig, value: unknown): string | null => {
  if (field.required && String(value ?? '').trim() === '') {
    return `${field.placeholder} is required`;
  }

  if (field.noWhitespace && hasInnerWhitespace(value)) {
    return INNER_WHITESPACE_ERROR;
  }

  return null;
};

export const validateDocumentFields = (schema: DeclarationDocumentSchema, isHighValue: boolean, values: DocumentFormValues): FormErrors => {
  const fields = schema[values.documentType]?.fields;
  if (!fields) return {};

  const declarationType = isHighValue ? FormDocumentsDeclarationType.HIGH_VALUES : FormDocumentsDeclarationType.LOW_VALUES;

  return Object.keys(fields)
    .filter((path) => isFieldRendered(fields[path], declarationType, values.header))
    .reduce<FormErrors>((errors, path) => {
      const error = getFieldError(fields[path], get(values, path));
      return error ? { ...errors, [path]: error } : errors;
    }, {});
};
